import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import type { ReportComponentKind } from '@/services/reports/reportTypes'

/**
 * Small drawings of what each kind of report component looks like on the
 * page. Drawn in `currentColor` so they follow the theme; the faint parts are
 * the same colour at lower opacity.
 */
const DRAWINGS: Record<ReportComponentKind, ReactNode> = {
  // A title, a subtitle and a couple of key/value lines.
  description: (
    <>
      <rect x="8" y="8" width="40" height="5" rx="1.5" />
      <rect x="8" y="17" width="26" height="3" rx="1" opacity="0.4" />
      <rect x="8" y="27" width="14" height="3" rx="1" opacity="0.4" />
      <rect x="26" y="27" width="30" height="3" rx="1" opacity="0.7" />
      <rect x="8" y="34" width="14" height="3" rx="1" opacity="0.4" />
      <rect x="26" y="34" width="22" height="3" rx="1" opacity="0.7" />
    </>
  ),
  // A row of stat tiles, each with a big number.
  totals: (
    <>
      {[6, 31, 56].map((x) => (
        <g key={x}>
          <rect x={x} y="10" width="19" height="28" rx="2.5" opacity="0.15" />
          <rect x={x + 4} y="15" width="9" height="2.5" rx="1" opacity="0.5" />
          <rect x={x + 4} y="22" width="12" height="6" rx="1.5" />
          <rect x={x + 4} y="31" width="7" height="2.5" rx="1" opacity="0.4" />
        </g>
      ))}
    </>
  ),
  // A table: header row and striped body rows.
  breakdown: (
    <>
      <rect x="6" y="8" width="68" height="6" rx="1.5" />
      {[17, 24, 31, 38].map((y, i) => (
        <g key={y} opacity={i % 2 ? 0.25 : 0.45}>
          <rect x="6" y={y} width="20" height="4" rx="1" />
          <rect x="30" y={y} width="12" height="4" rx="1" />
          <rect x="46" y={y} width="12" height="4" rx="1" />
          <rect x="62" y={y} width="12" height="4" rx="1" />
        </g>
      ))}
    </>
  ),
  // A line over a faint grid.
  chart: (
    <>
      <g opacity="0.2">
        <rect x="8" y="12" width="64" height="1" />
        <rect x="8" y="24" width="64" height="1" />
        <rect x="8" y="36" width="64" height="1" />
      </g>
      <path
        d="M8 34 L18 28 L28 31 L38 20 L48 23 L58 13 L72 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 38 L18 36 L28 37 L38 31 L48 33 L58 27 L72 29"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.35"
      />
    </>
  ),
  // A ranked list: labels with shrinking bars.
  top: (
    <>
      {[56, 44, 34, 24].map((w, i) => (
        <g key={w}>
          <rect x="6" y={9 + i * 8} width="10" height="4" rx="1" opacity="0.4" />
          <rect x="20" y={9 + i * 8} width={w} height="4" rx="1" opacity={1 - i * 0.2} />
        </g>
      ))}
    </>
  ),
  // Two ad thumbnails side by side.
  preview: (
    <>
      {[8, 44].map((x) => (
        <g key={x}>
          <rect x={x} y="9" width="28" height="22" rx="2" opacity="0.15" />
          <path
            d={`M${x + 4} 27 L${x + 11} 19 L${x + 16} 24 L${x + 19} 21 L${x + 24} 27 Z`}
            opacity="0.6"
          />
          <circle cx={x + 21} cy="15" r="2.5" opacity="0.6" />
          <rect x={x} y="35" width="20" height="3" rx="1" opacity="0.4" />
        </g>
      ))}
    </>
  ),
  // A paragraph of text.
  text: (
    <>
      <rect x="8" y="10" width="24" height="4" rx="1.5" />
      {[19, 25, 31].map((y) => (
        <rect key={y} x="8" y={y} width="64" height="3" rx="1" opacity="0.4" />
      ))}
      <rect x="8" y="37" width="40" height="3" rx="1" opacity="0.4" />
    </>
  ),
}

interface ReportComponentThumbnailProps {
  kind: ReportComponentKind
  className?: string
}

/** A miniature of how a report component looks in the finished report. */
export function ReportComponentThumbnail({ kind, className }: ReportComponentThumbnailProps) {
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-md border bg-(--ad-neutral-soft) text-(--ad-primary)',
        className
      )}
    >
      <svg viewBox="0 0 80 48" fill="currentColor" aria-hidden="true" className="h-full w-full">
        {DRAWINGS[kind]}
      </svg>
    </span>
  )
}
